import React, { useState } from 'react';
import { DATETIME_ELEMENT_STYLES, dateTimeFormat } from '@fiverr-private/localization';
import { Gql } from '@fiverr-private/dynamic_matching';
import { Container, Stack, Wrap } from '@fiverr-private/layout_components';
import { Radio, RadioGroup, Select, Option } from '@fiverr-private/forms';
import { Dropdown, DropdownAnchor, DropdownContent } from '@fiverr-private/dropdown';
import { Calendar } from '@fiverr-private/calendar';
import { DAY_IN_MS } from '@fiverr-private/futile';
import { Text } from '@fiverr-private/typography';
import { timeUnits, useBudgetAndTimelineContext, TimeUnit, spacingForControls, TimeUnitAndValue } from '../utils';
import { AppI18n, appTranslate } from '../../../../i18n';
import { useBriefCreationContext } from '../../../../context/BriefCreationContext';
import { CalendarTrigger } from './CalendarTrigger';

const generalTimelineValues = [1, 2, 3, 4, 5, 6, 7];
const deliveryTimelineCopyPrefix = 'sections.budget_and_timeline.delivery_timeline';
export const DeliveryTimelineSelector = () => {
  const { sectionContentState, setSectionContentState } = useBudgetAndTimelineContext();
  const { timelineDetails } = sectionContentState;
  const { generalTimeline = {} } = timelineDetails ?? {};
  const { enableEnhancedUx } = useBriefCreationContext();
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  const tomorrow = new Date(new Date().getTime() + DAY_IN_MS);
  const [selectedDate, setSelectedDate] = useState<Date | null>(
    sectionContentState.deliveryDate ? new Date(sectionContentState.deliveryDate) : tomorrow
  );

  const getFormattedDate = () =>
    sectionContentState.deliveryDate
      ? dateTimeFormat({
          value: Number(sectionContentState.deliveryDate),
          options: {
            day: DATETIME_ELEMENT_STYLES.TWO_DIGIT,
            month: DATETIME_ELEMENT_STYLES.TWO_DIGIT,
            year: DATETIME_ELEMENT_STYLES.NUMERIC,
          },
        })
      : '';

  const onChangedTimeline = (timeline: string) => {
    if (enableEnhancedUx && timeline === Gql.DmBriefTimeline.SpecificDate) {
      setIsCalendarOpen(true);
    }

    setSectionContentState({
      ...sectionContentState,
      timeline: timeline as Gql.DmBriefTimeline,
    });
  };

  const onChangedSpecificDate = (date: Date | null) => {
    setSectionContentState({
      ...sectionContentState,
      deliveryDate: date?.getTime() || tomorrow.getTime(),
    });
    setSelectedDate(date || tomorrow);
    setIsCalendarOpen(false);
  };

  const onGeneralTimelineChange = (newValue: Partial<TimeUnitAndValue>) => {
    setSectionContentState({
      ...sectionContentState,
      timelineDetails: {
        ...sectionContentState.timelineDetails,
        generalTimeline: {
          ...sectionContentState.timelineDetails?.generalTimeline,
          ...newValue,
        },
      },
    });
  };

  const onCalendarOpenChange = (isOpen: boolean) => {
    setIsCalendarOpen(isOpen);
  };

  const hideDateControl = sectionContentState.timeline !== Gql.DmBriefTimeline.SpecificDate;
  const hideGeneralTimelineSelector = sectionContentState.timeline !== Gql.DmBriefTimeline.GeneralTimeline;

  return (
    <Stack direction="column" paddingTop="1" gap="2">
      <Stack>
        <Text size="b_md" fontWeight="semibold">
          <AppI18n k={`${deliveryTimelineCopyPrefix}.title`} />
        </Text>
      </Stack>
      <Stack>
        <RadioGroup name="group-controlled" value={sectionContentState.timeline} onChange={onChangedTimeline}>
          {!enableEnhancedUx && (
            <Radio
              className={spacingForControls}
              name={Gql.DmBriefTimeline.SoonAsPossible}
              value={Gql.DmBriefTimeline.SoonAsPossible}
            >
              <Text size="b_sm">
                <AppI18n k={`${deliveryTimelineCopyPrefix}.asap`} />
              </Text>
            </Radio>
          )}
          <Radio
            className={spacingForControls}
            name={Gql.DmBriefTimeline.SpecificDate}
            value={Gql.DmBriefTimeline.SpecificDate}
          >
            <Text size="b_sm">
              <AppI18n k={`${deliveryTimelineCopyPrefix}.by_specific_date`} />
            </Text>
          </Radio>
          <Container className={spacingForControls} hidden={hideDateControl} width="240px">
            <Dropdown isOpen={isCalendarOpen} onOpenChanged={onCalendarOpenChange}>
              <DropdownAnchor fullWidth>
                <CalendarTrigger
                  placeholder={appTranslate(`${deliveryTimelineCopyPrefix}.choose_date`) as string}
                  value={getFormattedDate()}
                />
              </DropdownAnchor>
              <DropdownContent>
                <Calendar
                  allowedDatesRange={{
                    min: tomorrow,
                  }}
                  date={selectedDate}
                  calendarMode="single"
                  onDateChanged={onChangedSpecificDate}
                />
              </DropdownContent>
            </Dropdown>
          </Container>
          <Radio
            className={spacingForControls}
            name={Gql.DmBriefTimeline.GeneralTimeline}
            value={Gql.DmBriefTimeline.GeneralTimeline}
          >
            <Text size="b_sm">
              <AppI18n k={`${deliveryTimelineCopyPrefix}.general_timeline`} />
            </Text>
          </Radio>
          <Wrap direction="row" className={spacingForControls} hidden={hideGeneralTimelineSelector}>
            <Stack>
              <Stack alignItems="center">
                <Text size="b_sm">
                  <AppI18n k={`${deliveryTimelineCopyPrefix}.up_to`} />
                </Text>
              </Stack>
              <Stack width="90px">
                <Select
                  value={{ item: generalTimeline.value }}
                  onChange={(values) => onGeneralTimelineChange({ value: Number(values.item) })}
                  fullWidth={true}
                >
                  {generalTimelineValues.map((value) => (
                    <Option key={value} value={generalTimelineValues[value - 1]}>
                      {value}
                    </Option>
                  ))}
                </Select>
              </Stack>
            </Stack>
            <Stack width="200px">
              <Select
                value={{ item: generalTimeline.unit }}
                onChange={(values) => onGeneralTimelineChange({ unit: values.item as TimeUnit })}
                fullWidth={true}
              >
                {Object.keys(timeUnits).map((unit) => (
                  <Option key={unit} value={timeUnits[unit]}>
                    <AppI18n k={`${deliveryTimelineCopyPrefix}.${timeUnits[unit] as TimeUnit}`} />
                  </Option>
                ))}
              </Select>
            </Stack>
          </Wrap>
          <Radio
            className={spacingForControls}
            name={Gql.DmBriefTimeline.Flexible}
            value={Gql.DmBriefTimeline.Flexible}
          >
            <Text size="b_sm">
              <AppI18n k={`${deliveryTimelineCopyPrefix}.flexible`} />
            </Text>
          </Radio>
        </RadioGroup>
      </Stack>
    </Stack>
  );
};
